<script setup lang="ts">
import { computed, ref } from 'vue';
import { chartPaths, niceMax } from '../lib/resource-chart.js';

/**
 * A compact history chart. Two series at most ("in" and "out"), a shared time axis, and a
 * hover readout in the caption instead of a floating tooltip. Unknown points stay gaps.
 */
const props = defineProps<{
  title: string;
  series: readonly { label: string; values: readonly (number | null)[]; tone: 'in' | 'out' }[];
  start: number;
  step: number;
  format: (value: number) => string;
  /** Fixed scale, such as 100 for percentages; otherwise scaled to the data. */
  max?: number | undefined;
  floor?: number | undefined;
  reference?: { value: number; label: string } | undefined;
  windowLabel: string;
}>();

const length = computed(() => Math.max(0, ...props.series.map((s) => s.values.length)));
const top = computed(
  () =>
    props.max ??
    niceMax(
      Math.max(
        props.floor ?? 0,
        props.reference ? props.reference.value * 1.1 : 0,
        ...props.series.flatMap((s) => s.values.filter((v): v is number => v != null)),
      ),
    ),
);
const paths = computed(() => props.series.map((s) => chartPaths(s.values, top.value)));
const empty = computed(() => props.series.every((s) => s.values.every((v) => v == null)));
const hover = ref<number>();

function onMove(event: PointerEvent) {
  const box = (event.currentTarget as SVGElement).getBoundingClientRect();
  if (!box.width || length.value < 2) return;
  const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
  hover.value = Math.round(ratio * (length.value - 1));
}
function latest(values: readonly (number | null)[]) {
  for (let i = values.length - 1; i >= 0; i--) if (values[i] != null) return values[i]!;
  return null;
}
const readout = computed(() =>
  props.series.map((s) => {
    const value = hover.value == null ? latest(s.values) : s.values[hover.value];
    return { label: s.label, tone: s.tone, text: value == null ? '—' : props.format(value) };
  }),
);
const hoverTime = computed(() =>
  hover.value == null
    ? null
    : new Date(props.start + hover.value * props.step * 1000).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
);
const referenceY = computed(() =>
  props.reference ? 100 - (props.reference.value / top.value) * 96 : null,
);
const summary = computed(() => {
  const parts = props.series.map((s) => {
    const values = s.values.filter((v): v is number => v != null);
    if (!values.length) return `${s.label}: no data`;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    return `${s.label}: average ${props.format(mean)}, peak ${props.format(Math.max(...values))}`;
  });
  return `${props.title} over the last ${props.windowLabel}. ${parts.join('. ')}.`;
});
</script>

<template>
  <figure class="res-chart">
    <figcaption>
      <span class="res-chart-title">{{ title }}</span>
      <span class="res-chart-readout">
        <span v-if="hoverTime" class="res-chart-time">{{ hoverTime }}</span>
        <span v-for="item in readout" :key="item.label" class="res-chart-value">
          <span v-if="series.length > 1" class="res-dot" :data-tone="item.tone" />
          <span v-if="series.length > 1" class="sr-only">{{ item.label }}</span
          >{{ item.text }}
        </span>
      </span>
    </figcaption>
    <div class="res-chart-plot">
      <svg
        :viewBox="`0 0 ${Math.max(1, length - 1)} 100`"
        preserveAspectRatio="none"
        role="img"
        :aria-label="summary"
        @pointermove="onMove"
        @pointerleave="hover = undefined"
      >
        <line x1="0" :x2="Math.max(1, length - 1)" y1="100" y2="100" class="res-chart-base" />
        <line
          v-if="referenceY != null"
          x1="0"
          :x2="Math.max(1, length - 1)"
          :y1="referenceY"
          :y2="referenceY"
          class="res-chart-reference"
        />
        <template v-for="(path, i) in paths" :key="i">
          <path v-if="i === 0 && series.length === 1" :d="path.area" class="res-chart-area" />
          <path :d="path.line" class="res-chart-line" :data-tone="series[i]!.tone" />
        </template>
        <line
          v-if="hover != null"
          :x1="hover"
          :x2="hover"
          y1="0"
          y2="100"
          class="res-chart-cursor"
        />
      </svg>
      <span v-if="empty" class="res-chart-empty">Collecting…</span>
      <span v-if="reference" class="res-chart-reference-label">{{ reference.label }}</span>
    </div>
    <div class="res-chart-axis" aria-hidden="true">
      <span>−{{ windowLabel }}</span>
      <span>{{ max ? '' : `scale ${format(top)}` }}</span>
      <span>now</span>
    </div>
  </figure>
</template>
